import { useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import I18n from "i18n-js";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { useUpdateUserConfig, useUserConfig } from "app/api";
import { actions } from "app/configSlice";
import { AppDispatch, RootState } from "app/store";
import {
  Switch,
  RadioButton,
  ConfirmMessageBox,
  WarningMessageBox,
  DeleteConfirmMessageBox,
} from "common/elements";
import { btnDelay, usePlaySound } from "common/utils";
import {
  AppUser,
  UserType,
  AccountType,
  BillingType,
  BILLING_LIMIT_ACCOUNT,
} from "features/user/types";
import { deleteChild } from "features/user/api";

const languageMapping = {
  ja: " ひらがな",
  ja_kanji: "漢字",
};

const Title = ({ text }: { text: string }) => (
  <div className="flex-row-el w-[240px] items-center justify-end h-[80px]">
    <p className="text-body text-[20px] text-textcolor-black text-right leading-[30px]">
      {text}
    </p>
  </div>
);

export const UserSetting = ({ selectedUser }: { selectedUser: AppUser }) => {
  const { data: userConfig } = useUserConfig(selectedUser.uid);
  const { mutate: updateConfig } = useUpdateUserConfig(selectedUser.uid);
  const play = usePlaySound();
  const handleDialog = useDialog();
  const dispatch = useDispatch<AppDispatch>();
  const [change, setChange] = useState(false);
  const [remove, setRemove] = useState(false);
  const [changeMaxWarning, setChangeMaxWarning] = useState(false);
  const [changeMinWarning, setChangeMinWarning] = useState(false);
  const users = useSelector((state: RootState) => state.user.users);
  const parent = useSelector((state: RootState) => state.user.parent);
  const active = useSelector((state: RootState) => state.user.active);

  const handleChangeUserConfig =
    (uid: string, key: string, value: string | boolean) => () => {
      play();

      if (!userConfig) return;

      // アクティブユーザーの場合はReduxも更新
      if (uid === active?.uid) {
        dispatch(actions.updateUserConfig({ key, value }));
        if (key === "locale") {
          I18n.locale = value as string;
        }
      }

      // 新しいconfigを作成して更新
      const newConfig = {
        ...userConfig,
        [key]: value,
      };

      // react-queryのmutationを使ってキャッシュを更新
      updateConfig(newConfig);
    };

  const handleOpenChangeUserStatus = () => {
    btnDelay(() => {
      play();
      if (selectedUser.type === UserType.FROZEN) {
        if (parent.billing_type === BillingType.EDUCATION) {
          if (
            users.filter(
              (u) =>
                u.account_type !== AccountType.PARENTS &&
                u.type !== UserType.FROZEN,
            ).length >
            BILLING_LIMIT_ACCOUNT.EDUCATION - 1
          ) {
            setChangeMaxWarning(true);
          } else {
            setChange(true);
          }
        } else {
          if (
            users.filter(
              (u) =>
                u.account_type !== AccountType.PARENTS &&
                u.type !== UserType.FROZEN,
            ).length > 3
          ) {
            setChangeMaxWarning(true);
          } else {
            setChange(true);
          }
        }
      } else {
        if (
          users.filter(
            (u) =>
              u.account_type !== AccountType.PARENTS &&
              u.type !== UserType.FROZEN,
          ).length === 1
        ) {
          setChangeMinWarning(true);
        } else {
          setChange(true);
        }
      }
    });
  };
  const handleChangeUserStatus = () => {
    btnDelay(async () => {
      play();
      try {
        // TODO:
        setChange(false);
      } catch (error) {
        console.error(error);
        handleDialog({ type: DialogType.ERROR_NETWORK, value: true });
      }
    });
  };

  const handleOpenDeleteKidsUser = () => {
    btnDelay(() => {
      play();
      setRemove(true);
    });
  };
  const handleDeleteKidsUser = () => {
    btnDelay(async () => {
      play();
      try {
        dispatch(
          deleteChild({
            uid: selectedUser.uid,
            parent_uid: parent.uid,
          }),
        );
        toast(
          <div>
            <p className="text text-textcolor-black">
              {I18n.t("MSG_SETTING_DATA_DELETE_SUCCESS")}
            </p>
          </div>,
        );
        setRemove(false);
      } catch (error) {
        console.error(error);
        handleDialog({ type: DialogType.ERROR_NETWORK, value: true });
      }
    });
  };

  const handleCloseDialog = () => {
    btnDelay(() => {
      play();
      setChange(false);
      setRemove(false);
      setChangeMaxWarning(false);
      setChangeMinWarning(false);
    });
  };

  return (
    <>
      {change && (
        <ConfirmMessageBox
          title={
            selectedUser.type === UserType.FROZEN
              ? I18n.t("MSG_SETTING_PARENTS_ENABLE_KIDS_USER_TITLE")
              : I18n.t("MSG_SETTING_PARENTS_DISABLE_KIDS_USER_TITLE")
          }
          message={
            selectedUser.type === UserType.FROZEN
              ? I18n.t("MSG_SETTING_PARENTS_ENABLE_KIDS_USER_MESSAGE", {
                  name: selectedUser?.display_name,
                })
              : I18n.t("MSG_SETTING_PARENTS_DISABLE_KIDS_USER_MESSAGE", {
                  name: selectedUser?.display_name,
                })
          }
          close={handleCloseDialog}
          confirm={handleChangeUserStatus}
          is_parents
        />
      )}
      {remove && (
        <DeleteConfirmMessageBox
          title={I18n.t("MSG_SETTING_PARENTS_REMOVE_KIDS_USER_TITLE")}
          message={I18n.t("MSG_SETTING_PARENTS_REMOVE_KIDS_USER_MESSAGE", {
            name: selectedUser?.display_name,
          })}
          close={handleCloseDialog}
          confirm={handleDeleteKidsUser}
          is_parents
        />
      )}
      {changeMaxWarning && (
        <WarningMessageBox
          zIndex={9999999}
          title={I18n.t(
            "MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MAX_WARNING_TITLE",
          )}
          message={I18n.t(
            "MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MAX_WARNING_MESSAGE",
          )}
          close={handleCloseDialog}
        />
      )}
      {changeMinWarning && (
        <WarningMessageBox
          zIndex={9999999}
          title={I18n.t(
            "MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MIN_WARNING_TITLE",
          )}
          message={I18n.t(
            "MSG_SETTING_PARENTS_CHANGE_KIDS_USER_MIN_WARNING_MESSAGE",
          )}
          close={handleCloseDialog}
        />
      )}

      <div className="flex-col-el w-full gap-10 px-10 mt-10">
        <div className="flex-col-el flex-1 gap-10">
          <div className="flex-row-center h-[80px] gap-8">
            <Title text={I18n.t("MSG_SETTING_MENU_PROJECT_PUBLISH")} />
            <div className="flex-row-center">
              <Switch
                trackInactive="rgba(63, 74, 97, 0.5)"
                width={120}
                height={50}
                value={userConfig?.project_publish}
                onClick={handleChangeUserConfig(
                  selectedUser.uid,
                  "project_publish",
                  !userConfig?.project_publish,
                )}
              />
            </div>
          </div>
          <div className="flex-row-center h-[80px] gap-8">
            <Title text={I18n.t("MSG_SETTING_MENU_PROJECT_COPIED")} />
            <div className="flex-row-center">
              <Switch
                trackInactive="rgba(63, 74, 97, 0.5)"
                width={120}
                height={50}
                value={userConfig?.project_copied}
                onClick={handleChangeUserConfig(
                  selectedUser.uid,
                  "project_copied",
                  !userConfig?.project_copied,
                )}
              />
            </div>
          </div>
          <div className="flex-row-center h-[80px] gap-8">
            <Title text={I18n.t("MSG_SETTING_MENU_LANGUAGE")} />
            <div className="flex-col-el items-start gap-2">
              <div className="flex-row-center">
                <RadioButton
                  size={30}
                  value={userConfig?.locale === "ja"}
                  label={languageMapping["ja"]}
                  onChange={handleChangeUserConfig(
                    selectedUser.uid,
                    "locale",
                    "ja",
                  )}
                />
              </div>
              <div className="flex-row-center">
                <RadioButton
                  size={30}
                  value={userConfig?.locale === "ja_kanji"}
                  label={languageMapping["ja_kanji"]}
                  onChange={handleChangeUserConfig(
                    selectedUser.uid,
                    "locale",
                    "ja_kanji",
                  )}
                />
              </div>
            </div>
          </div>
          <div className="flex-row-center h-[80px] gap-8">
            <Title text={I18n.t("MSG_SETTING_APP_SETTING_USER_ACTIVE")} />
            <div className="flex-row-center">
              <Switch
                trackInactive="rgba(63, 74, 97, 0.5)"
                width={120}
                height={50}
                value={selectedUser.type !== UserType.FROZEN}
                onClick={handleOpenChangeUserStatus}
              />
            </div>
          </div>
        </div>

        <div className="flex-col-el flex-1 gap-10">
          <div className="flex-row-el flex-center alpha">
            <button
              disabled={selectedUser.uid === active.uid}
              className="btn btn-danger"
              onClick={handleOpenDeleteKidsUser}
            >
              <p className="text-body text-white text-[24px] !mb-[3px]">
                {I18n.t("MSG_PARENTS_KIDS_USER_DELETE_BTN")}
              </p>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
